/**
 * CreatePostFlowContext — Tracks whether the app is currently in a modal-initiated
 * "Create My Own Post" flow (e.g. from Say Hello welcome modal) where bottom tab
 * navigation is temporarily disabled and greyed out.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type PropsWithChildren,
} from 'react';

export interface CreatePostFlowContextValue {
  isCreateMyOwnPostFlow: boolean;
  setIsCreateMyOwnPostFlow: (active: boolean) => void;
  startCreateMyOwnPostFlow: () => void;
  exitCreateMyOwnPostFlow: () => void;
}

const CreatePostFlowContext = createContext<CreatePostFlowContextValue | null>(null);

export const CreatePostFlowProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [isCreateMyOwnPostFlow, setIsCreateMyOwnPostFlow] = useState(false);

  const startCreateMyOwnPostFlow = useCallback(() => {
    setIsCreateMyOwnPostFlow(true);
  }, []);

  const exitCreateMyOwnPostFlow = useCallback(() => {
    setIsCreateMyOwnPostFlow(false);
  }, []);

  const value = useMemo(
    () => ({
      isCreateMyOwnPostFlow,
      setIsCreateMyOwnPostFlow,
      startCreateMyOwnPostFlow,
      exitCreateMyOwnPostFlow,
    }),
    [isCreateMyOwnPostFlow, startCreateMyOwnPostFlow, exitCreateMyOwnPostFlow],
  );

  return (
    <CreatePostFlowContext.Provider value={value}>
      {children}
    </CreatePostFlowContext.Provider>
  );
};

export const useCreatePostFlow = (): CreatePostFlowContextValue => {
  const context = useContext(CreatePostFlowContext);
  if (!context) {
    return {
      isCreateMyOwnPostFlow: false,
      setIsCreateMyOwnPostFlow: () => {},
      startCreateMyOwnPostFlow: () => {},
      exitCreateMyOwnPostFlow: () => {},
    };
  }
  return context;
};
